// TODO update this to import from commmons
// import { gql } from '@reverbdotcom/commons/src/gql';
// eslint-disable-next-line no-restricted-imports
import { gql } from '@apollo/client';
import React from 'react';
import I18n from 'i18n-js';
import Modal from '@reverbdotcom/commons/src/components/modal';
import {
  BuyItNowPaymentOptionsFields,
  core_apimessages_CheckoutPaymentMethod_Type as CheckoutPaymentMethodType,
} from '@reverbdotcom/commons/src/gql/graphql';
import PaymentSelectionModalPaymentMethod from './payment_selection_modal_payment_method';
import { RCAlertBox, RCLoadingBars } from '@reverbdotcom/cadence/components';
import { CheckoutItemPageReturnError } from './checkout_item_page_return_layout';
import { useExpEnabled, useUser } from '@reverbdotcom/commons/src/user_hooks';
import BuyItNowExpressPayWrapper, { BuyItNowExpressPayWrapperFragment } from './BuyItNowExpressPayWrapper';

import { ErrorAlertBox } from '@reverbdotcom/commons/src/ErrorAlertBox';
import { groupedPaymentMethods } from './checkout/paymentMethodGroups';
import experiments from '@reverbdotcom/commons/src/experiments';

type Listing = BuyItNowPaymentOptionsFields.Fragment;

interface Props {
  listing: Listing;
  quantity: number;
  modalOpen: boolean;
  onRequestClose: () => any;
  onStartCheckoutSuccess: (redirectUrl: string, paymentMethodType: CheckoutPaymentMethodType) => any;
}

export function BuyItNowPaymentOptions(props: Props) {
  const [isLoading, setLoading] = React.useState(false);
  const [paymentMethodErrors, setPaymentMethodErrors] = React.useState([]);
  const [applePayError, setApplePayError] = React.useState('');
  const user = useUser();
  const { listing, quantity } = props;
  const standalonePayPalExp = useExpEnabled(experiments.STANDALONE_PAYPAL) &&
    !!listing.acceptedPaymentMethods.find(method => method === CheckoutPaymentMethodType.PAYPAL);
  const paymentMethods = groupedPaymentMethods(mapPaymentMethods(listing?.acceptedPaymentMethods), standalonePayPalExp);
  const showExpressPay = paymentMethods.express.length > 0;
  const buyerAndSellerInSameCountry = user.countryCode === listing.shop?.address?.countryCode;
  const sellerCountry = listing.shop?.address?.country?.name;
  const expressPaymentMethodTypes = paymentMethods.express.map((paymentMethod) => paymentMethod.type);

  const onApplePayError = React.useCallback((errorType: CheckoutItemPageReturnError, message: string) => {
    if (['PAYMENT_FAILURE', 'CHECKOUT_FAILURE'].includes(errorType)) {
      setApplePayError(message);
    }
  }, [setApplePayError]);

  if (!listing) return null;

  function onRequestClose() {
    setApplePayError('');
    setPaymentMethodErrors([]);
    props.onRequestClose();
  }

  return (
    <Modal
      isOpen={props.modalOpen}
      onRequestClose={onRequestClose}
      headerTitle={I18n.t('discovery.paymentMethodSelection.choosePaymentMethod')}
      paddedBody
    >
      {!buyerAndSellerInSameCountry &&
        <div className="mb-4">
          <RCAlertBox type="info">
            {I18n.t('discovery.checkout.buyItNow.notInSameCountry', {
              sellerCountry: sellerCountry,
            })}
          </RCAlertBox>
        </div>
      }
      {applePayError &&
        <div className="mb-4">
          <RCAlertBox
            data-apple-pay-alert-box
            type="error"
          >
            {applePayError}
          </RCAlertBox>
        </div>
      }
      {paymentMethodErrors.length > 0 &&
        <div className="mb-4">
          <ErrorAlertBox errors={paymentMethodErrors} />
        </div>
      }
      <div className="cart-payment-methods">
        {showExpressPay &&
          <BuyItNowExpressPayWrapper
            listing={listing}
            paymentMethods={expressPaymentMethodTypes}
            adyenCheckoutPaymentMethodsConfig={listing.adyenCheckoutPaymentMethodsConfig}
            setIsLoading={setLoading}
            isLoading={isLoading}
            onError={setPaymentMethodErrors}
            onApplePayError={onApplePayError}
            quantity={quantity}
          />
        }
        <ul>
          {paymentMethods.standard.map(paymentMethod =>
            <li key={paymentMethod.type} className="payment-method-button__wrapper">
              <PaymentSelectionModalPaymentMethod
                paymentMethod={paymentMethod.type}
                listingId={listing.id}
                onSuccess={props.onStartCheckoutSuccess}
                onError={setPaymentMethodErrors}
                setLoading={setLoading}
                isDisabled={isLoading}
                quantity={quantity}
              />
            </li>,
          )}
        </ul>
      </div>
      {isLoading &&
        <div className="d-flex fx-justify-center mtb-2">
          <RCLoadingBars />
        </div>
      }
    </Modal>
  );
}

function mapPaymentMethods(acceptedPaymentMethods: string[]) {
  if (!acceptedPaymentMethods) {
    return [];
  }

  return acceptedPaymentMethods.map((paymentMethod) => ({ type: paymentMethod.toUpperCase() as CheckoutPaymentMethodType }));
}

export const BuyItNowPaymentOptionsFragment = gql`
  fragment BuyItNowPaymentOptionsFields on Listing {
    _id
    acceptedPaymentMethods
    sellerId
    shop {
      _id
      address {
        _id
        countryCode
        country {
          _id
          name
        }
      }
    }
    shipping {
      localPickupOnly
    }
    pricing {
      buyerPrice {
        amount
        amountCents
      }
    }
    ...BuyItNowExpressPayWrapperFields
  }
  ${BuyItNowExpressPayWrapperFragment}
`;
